/**
 * The avatar editor (ENGAGEMENT_PLAN §5.1 F1) — a sample face (illustrated
 * SVG, no storage) or a letter-styled seal from the shared catalog, or an
 * uploaded square photo, or nothing (the deterministic initial circle). Shared by the onboarding screen and the profile screen, so
 * an avatar can be changed after onboarding, not just at first run.
 *
 * The photo is square-cropped to 512px JPEG client-side; the server only
 * enforces bytes. Every change invalidates the relationship tags so the
 * conversation list shows the new avatar immediately.
 */

import { useCallback, useRef, useState } from 'react';
import { FACE_IDS, FACE_LABELS, SEALS } from '@toiyp/shared';
import { useAppDispatch } from '../../store/hooks';
import { relationshipsApi } from '../relationships/api';
import type { AvatarView } from './api';
import { useClearAvatarMutation, useSetAvatarMutation } from './api';
import { Avatar } from './avatar';
import { FaceImage } from './faces';
import { track } from '../onboarding/track';

/** Square-crop the chosen file on a canvas (512px) so the server only enforces bytes. */
function fileToSquareJpeg(file: File, size = 512): Promise<{ data: string; mime: string }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const side = Math.min(img.naturalWidth, img.naturalHeight);
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('canvas unavailable');
        ctx.drawImage(img, (img.naturalWidth - side) / 2, (img.naturalHeight - side) / 2, side, side, 0, 0, size, size);
        resolve({ data: canvas.toDataURL('image/jpeg', 0.85).split(',')[1]!, mime: 'image/jpeg' });
      } catch (e) {
        reject(e instanceof Error ? e : new Error('could not read the image'));
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('that file is not an image'));
    };
    img.src = url;
  });
}

export function AvatarEditor({
  displayName,
  avatar,
  photoFirst = false,
}: {
  displayName: string;
  avatar: AvatarView | null;
  /** First run: lead with "Add a photo", the faces and seals after it. */
  photoFirst?: boolean;
}) {
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busySeal, setBusySeal] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [setAvatar, { isLoading: saving }] = useSetAvatarMutation();
  const [clearAvatar] = useClearAvatarMutation();
  const dispatch = useAppDispatch();

  // The conversation list caches peer profiles — an avatar change should show
  // there immediately, so invalidate the relationship tags after each change.
  const refreshConversations = useCallback(() => {
    dispatch(relationshipsApi.util.invalidateTags(['Relationship']));
  }, [dispatch]);

  const onSeal = useCallback(
    async (id: string) => {
      setError(null);
      setBusySeal(id);
      setStatus('Saving your seal…');
      try {
        await setAvatar({ kind: 'seal', id }).unwrap();
        refreshConversations();
        setStatus('Seal set.');
        track({ event: 'avatar_set', kind: 'seal' });
      } catch {
        setError('Could not set that seal — try again.');
        setStatus(null);
      } finally {
        setBusySeal(null);
      }
    },
    [setAvatar, refreshConversations],
  );

  const onFace = useCallback(
    async (id: string) => {
      setError(null);
      setBusySeal(id);
      setStatus('Saving your face…');
      try {
        await setAvatar({ kind: 'face', id }).unwrap();
        refreshConversations();
        setStatus('Face set.');
        track({ event: 'avatar_set', kind: 'face' });
      } catch {
        setError('Could not set that face — try again.');
        setStatus(null);
      } finally {
        setBusySeal(null);
      }
    },
    [setAvatar, refreshConversations],
  );

  const onFile = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = ''; // allow re-picking the same file
      setError(null);
      setStatus('Saving your photo…');
      try {
        const image = await fileToSquareJpeg(file);
        await setAvatar({ kind: 'upload', ...image }).unwrap();
        refreshConversations();
        setStatus('Photo saved.');
        track({ event: 'avatar_set', kind: 'upload' });
      } catch {
        setError('Could not read that image — try a PNG, JPEG, or WebP photo.');
        setStatus(null);
      }
    },
    [setAvatar, refreshConversations],
  );

  const onClear = useCallback(async () => {
    setError(null);
    setStatus('Removing your avatar…');
    try {
      await clearAvatar().unwrap();
      refreshConversations();
      setStatus('Avatar removed.');
    } catch {
      setError('Could not remove the avatar — try again.');
      setStatus(null);
    }
  }, [clearAvatar, refreshConversations]);

  const actionRow = (
    <div className="avatar-editor__actions">
      {/* A real button + programmatic input click — a <label> wrapper is not
          keyboard-focusable (UI/UX review pass, 2026-02-10). */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="visually-hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(e) => void onFile(e)}
      />
      <button
        type="button"
        className="btn btn--ghost avatar-editor__upload"
        disabled={saving}
        onClick={() => fileRef.current?.click()}
      >
        {saving ? 'Saving…' : photoFirst ? 'Add a photo' : 'Upload a photo'}
      </button>
      {avatar && (
        <button
          type="button"
          className="btn btn--ghost"
          disabled={saving}
          onClick={() => void onClear()}
        >
          Remove
        </button>
      )}
    </div>
  );

  return (
    <section className="avatar-editor" aria-label="Your avatar">
      <div className="avatar-editor__head">
        <span className="avatar-editor__preview">
          <Avatar name={displayName} avatar={avatar} label="Your avatar" />
        </span>
        <div className="avatar-editor__titlebox">
          <span className="avatar-editor__title">Your mark</span>
          <span className="avatar-editor__hint">
            Pick a face or a seal — both cost nothing; a photo is cropped to a square.
          </span>
        </div>
      </div>

      {photoFirst && actionRow}

      <div className="avatar-editor__faces" aria-label="Sample faces">
        {FACE_IDS.map((id) => (
          <button
            key={id}
            type="button"
            className={`avatar-editor__face${
              avatar?.kind === 'face' && avatar.url === id ? ' avatar-editor__face--on' : ''
            }`}
            aria-label={FACE_LABELS[id]}
            aria-pressed={avatar?.kind === 'face' && avatar.url === id}
            disabled={saving || busySeal === id}
            onClick={() => void onFace(id)}
          >
            <FaceImage id={id} />
          </button>
        ))}
      </div>

      <div className="avatar-editor__seals">
        {SEALS.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`avatar-editor__seal${
              avatar?.kind === 'seal' && avatar.url === s.id ? ' avatar-editor__seal--on' : ''
            }`}
            aria-label={s.name}
            aria-pressed={avatar?.kind === 'seal' && avatar.url === s.id}
            disabled={saving || busySeal === s.id}
            onClick={() => void onSeal(s.id)}
          >
            {s.glyph}
          </button>
        ))}
      </div>

      {!photoFirst && actionRow}

      {/* Live status: saving/result announced to screen readers. */}
      {status && (
        <p className="avatar-editor__status" role="status">
          {status}
        </p>
      )}

      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}